import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors } from "./payments.styles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enIN } from "date-fns/locale";
import dayjs from "dayjs";

const DATE_PRESETS = [
  { label: "Custom Range", value: "custom" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last 30 Days", value: "last30days" },
  { label: "This Month", value: "thisMonth" },
  { label: "Last Month", value: "lastMonth" },
  { label: "All Time", value: "allTime" },
];

const PaymentFilters = ({ onApply, onClose, initialFilters = {} }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  // Date range state - default to "All Time" to show all data
  const [datePreset, setDatePreset] = useState(initialFilters.datePreset || "allTime");
  const [dateRange, setDateRange] = useState({
    startDate: initialFilters.startDate || new Date(2020, 0, 1),
    endDate: initialFilters.endDate || dayjs().endOf("day").toDate(),
  });
  const [dateAnchor, setDateAnchor] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Service type
  const [serviceTypes, setServiceTypes] = useState(
    initialFilters.serviceTypes || { skin: true, dental: true, hair: true }
  );

  // Payment method
  const [paymentMethod, setPaymentMethod] = useState(initialFilters.paymentMethod || "all");

  // Amount range - empty by default to show all amounts
  const [amountRange, setAmountRange] = useState(
    initialFilters.amountRange || { min: "", max: "" }
  );

  // Payment status
  const [paymentStatus, setPaymentStatus] = useState(initialFilters.paymentStatus || "all");

  const handleDatePresetClick = (event) => {
    setDateAnchor(event.currentTarget);
  };

  const handleDatePresetClose = () => {
    setDateAnchor(null);
    setShowDatePicker(false);
  };

  const handlePresetSelect = (preset) => {
    setDatePreset(preset);
    const now = dayjs();
    let start, end;

    switch (preset) {
      case "today":
        start = now.startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "yesterday":
        start = now.subtract(1, "day").startOf("day").toDate();
        end = now.subtract(1, "day").endOf("day").toDate();
        break;
      case "last7days":
        start = now.subtract(7, "day").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "last30days":
        start = now.subtract(30, "day").startOf("day").toDate();
        end = now.endOf("day").toDate();
        break;
      case "thisMonth":
        start = now.startOf("month").toDate();
        end = now.endOf("month").toDate();
        break;
      case "lastMonth":
        start = now.subtract(1, "month").startOf("month").toDate();
        end = now.subtract(1, "month").endOf("month").toDate();
        break;
      case "allTime":
        start = new Date(2020, 0, 1);
        end = now.endOf("day").toDate();
        break;
      case "custom":
        setShowDatePicker(true);
        return;
      default:
        start = now.startOf("month").toDate();
        end = now.endOf("month").toDate();
    }

    setDateRange({ startDate: start, endDate: end });
    handleDatePresetClose();
  };

  const handleServiceToggle = (service) => {
    setServiceTypes((prev) => ({
      ...prev,
      [service]: !prev[service],
    }));
  };

  const handleClearAll = () => {
    setDatePreset("allTime");
    setDateRange({
      startDate: new Date(2020, 0, 1),
      endDate: dayjs().endOf("day").toDate(),
    });
    setServiceTypes({ skin: true, dental: true, hair: true });
    setPaymentMethod("all");
    setAmountRange({ min: "", max: "" });
    setPaymentStatus("all");
  };

  const handleApply = () => {
    onApply?.({
      datePreset,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      serviceTypes,
      paymentMethod,
      amountRange,
      paymentStatus,
    });
  };

  const formatDateDisplay = () => {
    const start = dayjs(dateRange.startDate).format("DD MMM YYYY");
    const end = dayjs(dateRange.endDate).format("DD MMM YYYY");
    return `${start} to ${end}`;
  };

  const checkboxSx = {
    color: colors.border,
    "&.Mui-checked": {
      color: primaryColor,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
  };

  const radioSx = {
    color: colors.border,
    "&.Mui-checked": {
      color: primaryColor,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 18,
    },
  };

  const sectionLabelSx = {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSecondary,
    letterSpacing: 0.3,
    mb: 1.5,
  };

  return (
    <Box sx={{ width: 380, p: 3 }}>
      {/* Date Range Section */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={sectionLabelSx}>DATE RANGE</Typography>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleDatePresetClick}
          endIcon={<KeyboardArrowDownIcon />}
          startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            fontSize: 13,
            borderColor: colors.border,
            color: colors.textPrimary,
            bgcolor: colors.bgPrimary,
            "& .MuiButton-endIcon": {
              ml: "auto",
            },
            "&:hover": {
              borderColor: primaryColor,
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          {formatDateDisplay()}
        </Button>

        {/* Date Preset Popover */}
        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={handleDatePresetClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
              overflow: "hidden",
            },
          }}
        >
          {showDatePicker ? (
            <Box>
              <DateRange
                ranges={[{
                  startDate: dateRange.startDate,
                  endDate: dateRange.endDate,
                  key: "selection",
                }]}
                locale={enIN}
                moveRangeOnFirstSelection={false}
                showMonthAndYearPickers={true}
                rangeColors={[primaryColor]}
                onChange={({ selection }) => {
                  setDateRange({
                    startDate: selection.startDate,
                    endDate: selection.endDate,
                  });
                }}
              />
              <Stack direction="row" spacing={2} p={2} pt={0}>
                <Button
                  variant="text"
                  onClick={() => setShowDatePicker(false)}
                  sx={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 500,
                    color: colors.textSecondary,
                    textTransform: "none",
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDatePresetClose}
                  sx={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    bgcolor: primaryColor,
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: 2,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: colors.primaryDark,
                      boxShadow: "none",
                    },
                  }}
                >
                  Apply
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ width: 200 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  px: 2,
                  py: 1.5,
                }}
              >
                SELECT RANGE
              </Typography>
              <List disablePadding>
                {DATE_PRESETS.map((preset) => (
                  <ListItem key={preset.value} disablePadding>
                    <ListItemButton
                      onClick={() => handlePresetSelect(preset.value)}
                      sx={{
                        py: 1.25,
                        px: 2,
                        "&:hover": {
                          bgcolor: colors.bgPrimary,
                        },
                      }}
                    >
                      <ListItemText
                        primary={preset.label}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: datePreset === preset.value ? 600 : 500,
                          color: datePreset === preset.value ? primaryColor : colors.textPrimary,
                        }}
                      />
                      {datePreset === preset.value && (
                        <CheckIcon sx={{ fontSize: 18, color: primaryColor }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Popover>
      </Box>

      {/* Service Type Section */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={sectionLabelSx}>SERVICE TYPE</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.5,
          }}
        >
          {[
            { key: "skin", label: "Skin Care" },
            { key: "dental", label: "Dental" },
            { key: "hair", label: "Hair Care" },
          ].map((service) => (
            <FormControlLabel
              key={service.key}
              control={
                <Checkbox
                  checked={serviceTypes[service.key]}
                  onChange={() => handleServiceToggle(service.key)}
                  sx={checkboxSx}
                />
              }
              label={service.label}
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.textPrimary,
                  ml: 0.5,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Payment Method Section */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={sectionLabelSx}>PAYMENT METHOD</Typography>
        <RadioGroup
          row
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          sx={{ gap: 3 }}
        >
          {["UPI", "Card", "Cash"].map((method) => (
            <FormControlLabel
              key={method}
              value={method.toLowerCase()}
              control={<Radio sx={radioSx} />}
              label={method}
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.textPrimary,
                  ml: 0.5,
                },
              }}
            />
          ))}
        </RadioGroup>
      </Box>

      {/* Amount Range Section */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={sectionLabelSx}>AMOUNT RANGE (₹)</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Min"
            size="small"
            type="number"
            value={amountRange.min}
            onChange={(e) => setAmountRange((prev) => ({ ...prev, min: e.target.value }))}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 14,
                bgcolor: "#fff",
                "& fieldset": {
                  borderColor: colors.border,
                },
                "&:hover fieldset": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1.25,
              },
            }}
          />
          <Typography sx={{ fontSize: 14, color: colors.textMuted }}>-</Typography>
          <TextField
            placeholder="Max"
            size="small"
            type="number"
            value={amountRange.max}
            onChange={(e) => setAmountRange((prev) => ({ ...prev, max: e.target.value }))}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 14,
                bgcolor: "#fff",
                "& fieldset": {
                  borderColor: colors.border,
                },
                "&:hover fieldset": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1.25,
              },
            }}
          />
        </Stack>
      </Box>

      {/* Payment Status Section */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={sectionLabelSx}>PAYMENT STATUS</Typography>
        <RadioGroup
          row
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          sx={{ gap: 3 }}
        >
          {["Success", "Pending", "Failed"].map((status) => (
            <FormControlLabel
              key={status}
              value={status.toLowerCase()}
              control={<Radio sx={radioSx} />}
              label={status}
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.textPrimary,
                  ml: 0.5,
                },
              }}
            />
          ))}
        </RadioGroup>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="text"
          onClick={handleClearAll}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: primaryColor,
            textTransform: "none",
            px: 2,
            "&:hover": {
              bgcolor: "transparent",
            },
          }}
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            bgcolor: primaryColor,
            color: "#fff",
            textTransform: "none",
            borderRadius: 3,
            py: 1.25,
            boxShadow: "none",
            "&:hover": {
              bgcolor: primaryColor,
              opacity: 0.9,
              boxShadow: "none",
            },
          }}
        >
          Apply Filters
        </Button>
      </Stack>
    </Box>
  );
};

export default PaymentFilters;
